import { useCallback, useEffect, useRef } from 'react';
import { Counter } from '../../components/Counter';
import { DatePicker } from '../DatePicker';
import { GuestPicker } from '../../components/GuestPicker';
import {
  CounterLabel,
  CounterRow,
  Dropdown as GuestDropdown,
} from '../../components/GuestPicker/GuestPicker.styles';
import { useDropdownGroup } from '../../hooks/useDropdownGroup';
import { CategorySearchIcon } from '../../icons/CategorySearchIcon';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { DotIcon } from '../../icons/DotIcon';
import { SearchSmallIcon } from '../../icons/SearchSmallIcon';
import {
  CategoryOption,
  CategoryThumb,
  CategoryThumbImg,
  ChevronIcon,
  PillIcon,
} from './JourneyPicker.styles';
import {
  Bar,
  CategoryDropdown,
  CategoryWrapper,
  ClearRateButton,
  DatesWrapper,
  DotSeparator,
  GuestsWrapper,
  Pill,
  PillLabel,
  RateDropdownShell,
  RateLabelText,
  RatePillGroup,
  RatePillLabelButton,
  RateWrapper,
  SearchPill,
  SearchWrapper,
} from './JourneyPickerDesktop.styles';
import { ClampedDropdownAnchor } from './ClampedDropdownAnchor';
import { RateCodeField } from './RateCodeField';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

const DROPDOWN_KEYS = ['category', 'dates', 'guests', 'rate'] as const;
type DropdownKey = (typeof DROPDOWN_KEYS)[number];

export interface JourneyPickerDesktopProps extends JourneyPickerSharedProps {
  columnView?: boolean;
  numberOfMonths?: 1 | 2;
}

export function JourneyPickerDesktop({
  showCategory,
  categories,
  category,
  currentCategoryLabel,
  onCategorySelect,
  fromDate,
  toDate,
  isSingleDate,
  datesLabel,
  onRangeSelect,
  displayGuests,
  guestMode,
  adults,
  children,
  maxAdults,
  maxChildren,
  guestsLabel,
  onAdultsChange,
  onChildrenChange,
  availability,
  isLoadingAvailability,
  showPastDates,
  onMonthChange,
  columnView,
  numberOfMonths,
  onSearch,
  searchLabel,
  showSearchButton = true,
  imageOverlay = false,
  datePickerLabels,
  guestPickerLabels,
  showRate,
  rateCode,
  rateLabels,
  onRateCodeChange,
}: JourneyPickerDesktopProps) {
  const { toggle, closeAll, setRef, isOpen } = useDropdownGroup<DropdownKey>(DROPDOWN_KEYS);

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      onRangeSelect(checkIn, checkOut);
      if (isSingleDate || checkOut) closeAll();
    },
    [onRangeSelect, isSingleDate, closeAll]
  );

  const handleCategoryClick = useCallback(
    (id: string) => {
      closeAll();
      onCategorySelect(id);
    },
    [closeAll, onCategorySelect]
  );

  const handleSearchClick = useCallback(() => {
    closeAll();
    onSearch();
  }, [closeAll, onSearch]);

  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const isFirstAutoSearchRun = useRef(true);

  useEffect(() => {
    if (isFirstAutoSearchRun.current) {
      isFirstAutoSearchRun.current = false;
      return;
    }
    if (showSearchButton) return;
    const isMobileViewport =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 600px)').matches
        : false;
    if (isMobileViewport) return;
    onSearchRef.current();
  }, [fromDate, toDate, adults, children, showSearchButton]);

  const handleRateApply = useCallback(
    (code: string) => {
      onRateCodeChange?.(code);
      closeAll();
    },
    [closeAll, onRateCodeChange]
  );

  const handleRateClear = useCallback(() => {
    onRateCodeChange?.('');
  }, [onRateCodeChange]);

  return (
    <Bar data-image-overlay={imageOverlay} data-journey-picker-bar="true">
      {showCategory && (
        <CategoryWrapper ref={setRef('category')}>
          <Pill type="button" $active={isOpen('category')} onClick={() => toggle('category')}>
            <PillIcon aria-hidden>
              <CategorySearchIcon size={20} />
            </PillIcon>
            <PillLabel data-text={currentCategoryLabel}>{currentCategoryLabel}</PillLabel>
            <ChevronIcon>
              <ChevronDownIcon />
            </ChevronIcon>
          </Pill>

          {isOpen('category') && (
            <ClampedDropdownAnchor wide>
              <CategoryDropdown>
                {categories!.map((cat) => (
                  <CategoryOption
                    key={cat.id}
                    type="button"
                    $active={cat.id === category}
                    onClick={() => handleCategoryClick(cat.id)}
                  >
                    <span>{cat.label}</span>
                    <CategoryThumb>
                      {cat.imageUrl && (
                        <CategoryThumbImg src={cat.imageUrl} alt={cat.imageAlt ?? ''} />
                      )}
                    </CategoryThumb>
                  </CategoryOption>
                ))}
              </CategoryDropdown>
            </ClampedDropdownAnchor>
          )}
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>
        </CategoryWrapper>
      )}

      <DatesWrapper ref={setRef('dates')} $isFirst={!showCategory}>
        <Pill
          type="button"
          $active={isOpen('dates')}
          aria-expanded={isOpen('dates')}
          data-journey-picker-edit-dates
          onClick={() => toggle('dates')}
        >
          <PillLabel data-text={datesLabel}>{datesLabel}</PillLabel>
        </Pill>

        {isOpen('dates') && (
          <ClampedDropdownAnchor>
            <DatePicker
              checkIn={fromDate || null}
              checkOut={isSingleDate ? null : toDate || null}
              singleDateMode={isSingleDate}
              availability={availability}
              isLoading={isLoadingAvailability}
              showPastDates={showPastDates}
              onRangeSelect={handleRangeSelect}
              onMonthChange={onMonthChange}
              columnView={columnView}
              numberOfMonths={numberOfMonths}
              labels={datePickerLabels}
            />
          </ClampedDropdownAnchor>
        )}
      </DatesWrapper>

      {displayGuests && (
        <>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>

          {guestMode === 'combined' ? (
            <GuestsWrapper ref={setRef('guests')}>
              <Pill type="button" $active={isOpen('guests')} onClick={() => toggle('guests')}>
                <PillLabel data-text={guestsLabel}>{guestsLabel}</PillLabel>
              </Pill>

              {isOpen('guests') && (
                <ClampedDropdownAnchor wide>
                  <GuestDropdown $asInline $withShadow>
                    <CounterRow>
                      <CounterLabel>{guestPickerLabels?.guests ?? 'Guests'}</CounterLabel>
                      <Counter
                        value={adults}
                        min={1}
                        max={maxAdults ?? 20}
                        onChange={onAdultsChange}
                        decreaseLabel={guestPickerLabels?.decreaseAdults}
                        increaseLabel={guestPickerLabels?.increaseAdults}
                      />
                    </CounterRow>
                  </GuestDropdown>
                </ClampedDropdownAnchor>
              )}
            </GuestsWrapper>
          ) : (
            <GuestsWrapper ref={setRef('guests')}>
              <Pill type="button" $active={isOpen('guests')} onClick={() => toggle('guests')}>
                <PillLabel data-text={guestsLabel}>{guestsLabel}</PillLabel>
              </Pill>

              {isOpen('guests') && (
                <ClampedDropdownAnchor wide>
                  <GuestPicker
                    adults={adults}
                    childCount={children}
                    maxAdults={maxAdults}
                    maxChildren={maxChildren}
                    onAdultsChange={onAdultsChange}
                    onChildrenChange={onChildrenChange}
                    renderInline
                    showShadow
                    labels={guestPickerLabels}
                  />
                </ClampedDropdownAnchor>
              )}
            </GuestsWrapper>
          )}
        </>
      )}
      {showRate && onRateCodeChange && (
        <>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>
          <RateWrapper ref={setRef('rate')}>
            {rateCode ? (
              <RatePillGroup>
                <RatePillLabelButton type="button" onClick={() => toggle('rate')}>
                  <RateLabelText title={rateCode}>{rateCode}</RateLabelText>
                </RatePillLabelButton>
                <ClearRateButton
                  type="button"
                  aria-label={rateLabels.clear}
                  onClick={handleRateClear}
                >
                  ×
                </ClearRateButton>
              </RatePillGroup>
            ) : (
              <Pill type="button" $active={isOpen('rate')} onClick={() => toggle('rate')}>
                <PillLabel data-text={rateLabels.pill}>{rateLabels.pill}</PillLabel>
              </Pill>
            )}
            {isOpen('rate') && (
              <ClampedDropdownAnchor>
                <RateDropdownShell>
                  <RateCodeField
                    value={rateCode}
                    placeholder={rateLabels.placeholder}
                    applyLabel={rateLabels.apply}
                    onApply={handleRateApply}
                  />
                </RateDropdownShell>
              </ClampedDropdownAnchor>
            )}
          </RateWrapper>
        </>
      )}
      {showSearchButton && (
        <SearchWrapper>
          <SearchPill type="button" onClick={handleSearchClick} aria-label={searchLabel}>
            <SearchSmallIcon size={24} />
          </SearchPill>
        </SearchWrapper>
      )}
    </Bar>
  );
}
